import { TZDate } from '@date-fns/tz';
import type { Timezone } from 'next-intl';
import type { DrawingLotteryType, ResultsByRangeQueryParams } from '../types';

type ResultsRangeSelection = {
  year: number;
  month: number;
  day?: number;
  hour?: number;
};

function padDatePart(value: number): string {
  return String(value).padStart(2, '0');
}

function toUtcDateTimeString(year: number, month: number, day: number, hour = 0, minute = 0): string {
  return `${year}-${padDatePart(month)}-${padDatePart(day)}T${padDatePart(hour)}:${padDatePart(minute)}:00`;
}

/**
 * Converts a local wall-clock date-time in `timeZone` to a UTC date-time string (no offset).
 * LRS `getResultsByRange` expects `from`/`to` in this form.
 */
function zonedLocalDateTimeToUtcString(year: number, month: number, day: number, hour: number, minute: number, timeZone: Timezone | undefined): string {
  if (!timeZone || timeZone === 'UTC') {
    return toUtcDateTimeString(year, month, day, hour, minute);
  }

  const utc = new TZDate(year, month - 1, day, hour, minute, timeZone);
  return toUtcDateTimeString(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate(), utc.getUTCHours(), utc.getUTCMinutes());
}

function addCalendarDays(year: number, month: number, day: number, daysToAdd: number): { year: number; month: number; day: number } {
  const date = new Date(Date.UTC(year, month - 1, day + daysToAdd));
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

/**
 * Builds inclusive/exclusive `[from, to)` query params for `GET .../results/{lottery}/range`.
 * Local calendar bounds are converted to UTC using the user's timezone.
 * - NORMAL: one calendar month
 * - GO: one calendar day
 * - HIGH_FREQUENCY: one clock hour
 */
function buildResultsRangeParams(lotteryType: DrawingLotteryType, selection: ResultsRangeSelection, timeZone?: Timezone): ResultsByRangeQueryParams | undefined {
  const { year, month, day, hour } = selection;

  if (lotteryType === 'NORMAL') {
    const from = zonedLocalDateTimeToUtcString(year, month, 1, 0, 0, timeZone);
    const next = month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
    const to = zonedLocalDateTimeToUtcString(next.year, next.month, 1, 0, 0, timeZone);
    return { from, to };
  }

  if (day === undefined) return undefined;

  if (lotteryType === 'GO') {
    const from = zonedLocalDateTimeToUtcString(year, month, day, 0, 0, timeZone);
    const next = addCalendarDays(year, month, day, 1);
    const to = zonedLocalDateTimeToUtcString(next.year, next.month, next.day, 0, 0, timeZone);
    return { from, to };
  }

  if (hour === undefined) return undefined;

  const from = zonedLocalDateTimeToUtcString(year, month, day, hour, 0, timeZone);
  if (hour >= 23) {
    const next = addCalendarDays(year, month, day, 1);
    return { from, to: zonedLocalDateTimeToUtcString(next.year, next.month, next.day, 0, 0, timeZone) };
  }

  return { from, to: zonedLocalDateTimeToUtcString(year, month, day, hour + 1, 0, timeZone) };
}

export { buildResultsRangeParams, padDatePart, zonedLocalDateTimeToUtcString };
export type { ResultsRangeSelection };
